/* Common Imports */
import { ObjectId } from "bson";
import React from "react";

/* Redux Imports */
import StateInterface from "../../../redux-magic/state-interface";
import {
	BusinessProfileInterface,
	PropertyInterface,
	PropertyMediaImage,
	PropertySnackbarInterface,
	UserInterface,
} from "../../../redux-magic/sub-interfaces/sub-interfaces";
import {
	CropPropertyImageThunk,
	UpdatePropertyImageThunk,
	deletePropertyImageThunk,
	setPropertySnackbarThunk,
} from "../../../redux-magic/thunks";

/* router imports */
import { useRouter } from "next/router";

/* Component Imports */
import BeegruButton from "../../common-components/buttons/BeegruButton";
import ProgressBar from "../../page-sections/SingleProperty/sub-components/CarouselContent/ProgressBar";
import UploadImagesMobile from "./UploadPropertyImagesMobile";

/* Library Imports */
import ReactCrop, { PercentCrop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import getCroppedImg from "../../../lib/CropImage";
import uploadFiles from "../../../lib/uploadFiles";

/* mui imports */
import {
	Alert,
	Chip,
	CircularProgress,
	Dialog,
	FormControl,
	IconButton,
	MenuItem,
	RadioGroup,
	Select,
	SelectChangeEvent,
	Slide,
	Snackbar,
	Typography,
	useTheme,
} from "@mui/material";
import { styled, useMediaQuery } from "@mui/system";

/* Icon Imports */
import { ArrowBack, ChevronLeft, ChevronRight, Close, DeleteOutline } from "@mui/icons-material";
import CropIcon from "@mui/icons-material/Crop";
import { TransitionProps } from "@mui/material/transitions";

/* Interfaces */

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

interface FileAndMore {
	file: File;
	mime_type: string;
}

/* Styles */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
		},
		// minHeight: "700px",
		maxHeight: "100%",
		borderRadius: "1rem",
		background: theme.palette.background.paper,
	},
}));

const CustomDialogTitle = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 1rem 0.5rem 1rem",
	background: theme.palette.background.default,
}));

const CustomDialogContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "100%",
	height: "100%",
	gap: "1rem",
	background: theme.palette.background.default,
	overflow: "scroll",
	scrollbarWidth: "none",
	padding: "1rem",
}));

const CustomDialogActions = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
	padding: " 1rem",
	background: theme.palette.background.default,
}));

const ImagePreviewContainer = styled(RadioGroup)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	maxHeight: "30rem",
	gap: "1rem",
	flexWrap: "wrap",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "47%",
	height: "10.5rem",
	borderRadius: "16px",
	borderColor: theme.palette.primary.main,
}));

const ImageContainer = styled("div")(({ theme }) => ({
	position: "relative",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
}));

const PreviewImage = styled("img")(({ theme }) => ({
	width: "100%",
	height: "10.238rem",
	borderRadius: "16px ",
	// border: "3px solid rgba(0, 0, 0, 0.23)",
	// borderColor: theme.palette.primary.main,
}));

const FormControlContainer = styled(FormControl)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	padding: "var(--Spacing-spacing-sm, 0.5rem)",
	gap: "var(--Spacing-spacing-sm, 0.5rem)",
	alignSelf: "stretch",
	width: "100%",
	height: "3rem",
	position: "absolute",
	bottom: "0.3rem",
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	// [theme.breakpoints.down("md")]: {
	// 	bottom: "5rem",
	// 	borderRadius: "0.5rem",
	// },
}));

const Images = styled("img")(({ theme }) => ({
	objectFit: "contain",
	width: "100%",
	height: "100%",
}));

const TextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
	marginBottom: "0.25rem",
}));

const InputContaiiner = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
}));

const CropContainer = styled(ReactCrop)(({ theme }) => ({}));

/* Dialog Transition */

const DialogTransition = React.forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<any, any>;
	},
	ref: React.Ref<unknown>,
) {
	return (
		<Slide
			direction="up"
			ref={ref}
			{...props}
		/>
	);
});

const EditModalMobile = ({
	openModal,
	handleClose,
	propertyData,
	dispatch,
	session,
	index,
	isEditor,
	profile_context,
	property_snackbar,
}: {
	openModal: boolean;
	handleClose: any;
	propertyData: LocalPropertyInterface;
	dispatch: Function;
	session: any;
	index: number;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	property_snackbar: PropertySnackbarInterface;
}) => {
	const theme = useTheme();

	/*

  & Let's declare the router. Here we are using the useRouter hook from nextjs.

  & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

  */

	const [is_cropped, setIsCropped] = React.useState<boolean>(true);

	const router = React.useRef(useRouter()).current;

	const localLang = {
		heading: router.locale === "ar-AE" ? " " : router.locale === "kn-IN" ? "ಚಿತ್ರಗಳನ್ನು ಸಂಪಾದಿಸಿ" : "Edit Images",
		save: router.locale === "ar-AE" ? " " : router.locale === "kn-IN" ? "ಉಳಿಸಿ" : "Save",
		crop: router.locale === "ar-AE" ? " " : router.locale === "kn-IN" ? "ಕ್ರಾಪ್ ಮಾಡಿ" : "Crop",
		originalImage: router.locale === "ar-AE" ? " " : router.locale === "kn-IN" ? "ಮೊಲದ ಚಿತ್ರ" : "Original",
		croppedImage: router.locale === "ar-AE" ? " " : router.locale === "kn-IN" ? "ಕ್ರಾಪ್ ಮಾಡಿದ ಚಿತ್ರ" : "Cropped Image",
		uploadImages:
			router.locale === "ar-AE" ? " " : router.locale === "kn-IN" ? "ಚಿತ್ರಗಳನ್ನು ಅಪ್ಲೋಡ್ ಮಾಡಿ" : "Upload Images",
	};

	const imagesCleanedUp = propertyData.images.map((image) => ({
		...image,
		url: typeof image.url === "object" ? (image.url as PropertyMediaImage).regular : image.url,
		url_from_server: image.url,
	}));

	const originalImage: Array<string> = imagesCleanedUp.map((image) =>
		image.url_from_server ? (image.url_from_server as PropertyMediaImage).original_file_url : "",
	);

	const [deleted, setDeleted] = React.useState<boolean>(false);

	const [isEdit, setIsEdit] = React.useState<boolean>(false);

	const [saveCrop, setSaveCrop] = React.useState<boolean>(false);

	const [open, setOpen] = React.useState<boolean>(false);

	const [show, setShow] = React.useState<boolean>(false);

	const [number, setNumber] = React.useState<number>(0);

	const [caption, setCaption] = React.useState<string>("");

	// const [category, setCategory] = React.useState<string>(propertyData.images[number]?.category);

	const [category, setCategory] = React.useState<Array<string>>(
		propertyData.images.map((image) => (image.category ? image.category : "all_images")),
	);

	/*

	& The order the photos show and are saved in (indices into propertyData.images, which crop, delete and category keep
	& using), cover first (Listings #42, the app's PhotoReorder; the desktop modal's twin). The arrows move a photo
	& (touch has no drag-and-drop in the browser); a tap still makes a photo the cover by moving it to the front.
	& A delete, crop or upload reloads the photos, and the order is read again from their priorities.

	*/

	const savedOrder = (): Array<number> =>
		propertyData.images
			.map((_image, index) => index)
			.sort((a, b) => (propertyData.images[a].priority ?? 1) - (propertyData.images[b].priority ?? 1));

	const [order, setOrder] = React.useState<Array<number>>(savedOrder);

	React.useEffect(() => {
		setOrder(savedOrder());
		// The photos are the only input; savedOrder reads them.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [propertyData.images]);

	const priority: number = order.length > 0 ? order[0] : 0;

	/* Moves photo [imageIndex] to display position [toPosition]. */

	const movePhoto = (imageIndex: number, toPosition: number) => {
		setOrder((prev) => {
			const from = prev.indexOf(imageIndex);
			if (from < 0 || toPosition < 0 || toPosition >= prev.length || from === toPosition) return prev;
			const next = [...prev];
			next.splice(from, 1);
			next.splice(toPosition, 0, imageIndex);
			return next;
		});
		setSave(true);
	};

	const [selectedFiles, setSelectedFiles] = React.useState<Array<FileAndMore>>([]);

	const [percentage, setPercentage] = React.useState<Array<number>>([0]);

	const [stage, setStage] = React.useState<Array<string>>(["NOT_STARTED"]);

	const [fileId, setFileId] = React.useState<Array<ObjectId | string>>([""]);

	const [loading, setLoading] = React.useState<boolean>(false);

	const [editSelect, setEditSelect] = React.useState<string>("home");

	const [save, setSave] = React.useState<boolean>(false);

	const smDevice: boolean = useMediaQuery(theme.breakpoints.down("sm"));

	const [completedCrop, setCompletedCrop] = React.useState<PercentCrop>({
		unit: "%",
		x: 25,
		y: 25,
		width: 50,
		height: 28.125, // Updated height for 16:9 aspect ratio
	});

	const [crop, setCrop] = React.useState<PercentCrop>({
		unit: "%",
		x: 25,
		y: 25,
		width: 50,
		height: 28.125, // Updated height for 16:9 aspect ratio
	});

	const [duri, setDuri] = React.useState<string>("");

	const [openProfileModal, setOpenProfileModal] = React.useState(false);

	let myFileId: Array<ObjectId | string>;

	/*

  & Next, let's store the original image url in a variable. We will use this variable to display the original image in the modal.

  */

	const ogUrl: string = originalImage.length > 0 ? originalImage[number] : "";

	interface UploadProgress {
		fileId: ObjectId | string;
		percentage: number;
		stage: string;
	}

	/* Next, Let's create a function to select the category for the selected image */
	const handleSelect = (event: SelectChangeEvent<string>, index: number) => {
		// Let's check if the value is not empty or undefined
		// and then set the category to the selected value
		if (event.target.value !== "" && event.target.value !== undefined) {
			setCategory((prev) => {
				const updated = [...prev];
				updated[index] = event.target.value.replace(" ", "_").toLowerCase();
				return updated;
			});
		} else {
			// If the value is empty or undefined, set the category to "exterior"
			setCategory((prev) => {
				const updated = [...prev];
				updated[index] = "exterior";
				return updated;
			});
		}
	};

	// Let's create a function to format the category name
	// and make it more readable
	const formatCategory = (category: string) => {
		return category
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	const handleCrop = (index: number) => {
		setEditSelect("edit-crop");
		setNumber(index);
	};

	/* Submit function to save the images to the property collection */
	const handleSaveTextData = async () => {
		setDeleted(true);
		/* Sent in the shown order; the first is the cover. */
		const image_mime_type: Array<string | undefined> = order.map((index) => propertyData.images[index].mime_type);
		const fileId: Array<ObjectId | string> = order.map((index) => propertyData.images[index].file_id);
		setIsEdit(!isEdit);
		await dispatch(
			UpdatePropertyImageThunk({
				property_id: propertyData._id,
				file_id: fileId,
				prev_id: fileId,
				mime_type: image_mime_type,
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				caption: caption,
				category: order.map((index) => category[index]),
				priority: 0,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: propertyData.created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				profile_context: profile_context,
			}),
		);
		setDeleted(false);
		handleModalClose();
	};

	/* Image crop function */

	const onCropChange = (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
		const cropdata = crop;
		if (cropdata.width !== percentCrop.width) {
			setIsCropped(false);
		}
		setCrop(percentCrop);
	};

	const onCropComplete = (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
		setCompletedCrop(percentCrop);
	};

	const getImageDimensions: Function = (url: string): Promise<{ width: number; height: number }> => {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.src = url;
			img.onload = () => resolve({ width: img.width, height: img.height });
			img.onerror = (err) => reject(err);
		});
	};

	const handleEdit = () => {
		setSaveCrop(!saveCrop);
		setOpen(!open);
		const debouncedExecutor = async () => {
			const { width, height } = await getImageDimensions(ogUrl);
			const pixelWidth: number = width * (completedCrop.width / 100);
			const pixelHeight: number = (9 * pixelWidth) / 16;
			const croppedAreaPixels = {
				x: Math.round(width * (completedCrop.x / 100)),
				y: Math.round(height * (completedCrop.y / 100)),
				width: Math.round(pixelWidth),
				height: Math.round(pixelHeight),
			};

			const dataUri = await getCroppedImg(ogUrl, croppedAreaPixels);
			setDuri(dataUri);
			const blob = await (await fetch(dataUri)).blob();
			const mt: string = imagesCleanedUp[number]
				? imagesCleanedUp[number].url_from_server
					? (imagesCleanedUp[number].url_from_server as PropertyMediaImage).original_file_mime_type
						? (imagesCleanedUp[number].url_from_server as PropertyMediaImage).original_file_mime_type
						: "image/jpeg"
					: "image/jpeg"
				: "image/jpeg";
			const file = new File([blob], mt.replace("/", "."), {
				type: mt,
			});
			setSelectedFiles([{ file: file, mime_type: mt }]);
		};
		const debounced = setTimeout(debouncedExecutor, 500);
		return () => clearTimeout(debounced);
	};

	const handleUpload = async () => {
		setShow(!show);
		await uploadFiles(
			session ? (session.authorized ? (session.session_id ? session.session_id : "") : "") : "",
			selectedFiles.map((file: FileAndMore) => ({
				file: file.file,
				mime_type: file.mime_type,
				original_file_id: propertyData
					? propertyData.images
						? propertyData.images[number]
							? propertyData.images[number].url
								? (propertyData.images[number].url as PropertyMediaImage).original_file_id
									? new ObjectId((propertyData.images[number].url as PropertyMediaImage).original_file_id)
									: new ObjectId()
								: new ObjectId()
							: new ObjectId()
						: new ObjectId()
					: new ObjectId(),
				original_file_server_path: propertyData
					? propertyData.images
						? propertyData.images[number]
							? propertyData.images[number].url
								? (propertyData.images[number].url as PropertyMediaImage).original_file_server_path
									? (propertyData.images[number].url as PropertyMediaImage).original_file_server_path
									: ""
								: ""
							: ""
						: ""
					: "",
				original_file_mime_type: propertyData
					? propertyData.images
						? propertyData.images[number]
							? propertyData.images[number].url
								? (propertyData.images[number].url as PropertyMediaImage).original_file_mime_type
									? (propertyData.images[number].url as PropertyMediaImage).original_file_mime_type
									: ""
								: ""
							: ""
						: ""
					: "",
				original: false,
				is_business_profile: propertyData
					? propertyData.created_by
						? propertyData.created_by.is_business_profile
						: false
					: false,
				business_profile_id: propertyData
					? propertyData.created_by
						? propertyData.created_by.business_profile_id
							? (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id
								? new ObjectId((propertyData.created_by.business_profile_id as BusinessProfileInterface)._id)
								: new ObjectId()
							: new ObjectId()
						: new ObjectId()
					: new ObjectId(),
				entity: "property_" + (propertyData ? (propertyData.is_ofb ? "ofb" : "regular") : "regular"),
				entity_id: propertyData ? (propertyData._id ? new ObjectId(propertyData._id) : new ObjectId()) : new ObjectId(),
				use_case: "property_image",
				uiPath: "/",
				isPublic: false,
				text: propertyData.created_by.is_business_profile
					? (propertyData.created_by.business_profile_id as BusinessProfileInterface).title
					: (propertyData.created_by.user_id as UserInterface).first_name +
						" " +
						(propertyData.created_by.user_id as UserInterface).last_name,
			})),
			(uploadProgress: Array<UploadProgress>) => {
				setFileId(uploadProgress.map((progress) => progress.fileId));
				setPercentage(uploadProgress.map((progress) => progress.percentage));
				setStage(uploadProgress.map((progress) => progress.stage));
				myFileId = uploadProgress.map((progress) => progress.fileId);
			},
		);
		handleSaveData();
	};

	/* Upload cropped image data */

	const handleSaveData = async () => {
		const image_mime_type: string | undefined = propertyData.images[number].mime_type;
		setIsEdit(!isEdit);
		setShow(!show);
		setLoading(!loading);
		await dispatch(
			CropPropertyImageThunk({
				property_id: propertyData._id,
				file_id: myFileId[0],
				prev_id: propertyData.images[number].file_id,
				mime_type: image_mime_type,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: propertyData.created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
			}),
		);
		handleBack();
	};

	/* Delete image function */
	const handleDeleteImage = async (index: number) => {
		const newCategory = [...category];
		newCategory.splice(index, 1);
		setCategory(newCategory);

		setDeleted(true);
		await dispatch(
			deletePropertyImageThunk({
				property_id: propertyData._id,
				file_id: propertyData.images[index].file_id,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: propertyData.created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				profile_context: profile_context,
			}),
		);
		setDeleted(false);
	};

	/* Upload Picture modal */

	const handleUploadModalOpen = () => {
		setOpenProfileModal(true);
	};

	const handleUploadModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setOpenProfileModal(false);
		}
	};

	const handleBack = () => {
		setSaveCrop(false);
		setDuri("");
		setSelectedFiles([]);
		setPercentage([0]);
		setStage(["NOT_STARTED"]);
		setShow(false);
		setIsEdit(false);
		setOpen(false);
		setEditSelect("home");
		setLoading(false);
		setDeleted(false);
		setOrder(savedOrder());
	};

	const handleModalClose = () => {
		setSave(false);
		handleBack();
		handleClose();
	};

	React.useEffect(() => {
		if (propertyData.images.length < 1 && deleted) {
			handleClose();
		}
	}, [propertyData.images.length, deleted, handleClose]);

	React.useEffect(() => {
		if (openModal) {
			setCategory(
				propertyData.images.map((image, index) =>
					category[index] !== undefined && category[index] !== null ? category[index] : image.category,
				),
			);
		}
	}, [openModal, propertyData.images, category]);

	return (
		<React.Fragment>
			<Snack
				open={
					property_snackbar
						? (property_snackbar.property_images_edit as boolean) ||
							(property_snackbar.property_images_delete as boolean)
						: false
				}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{property_snackbar
						? property_snackbar.property_images_edit
							? "Image edited successfully"
							: property_snackbar.property_images_delete
								? "Image deleted successfully"
								: "Image uploaded successfully"
						: "success"}
				</Alert>
			</Snack>
			<CustomDialog
				open={openModal}
				maxWidth="xs"
				fullWidth={true}
				fullScreen={smDevice}
				TransitionComponent={DialogTransition}
			>
				<CustomDialogTitle variant="h6">
					<div>
						{editSelect === "edit-crop" ? (
							<IconButton onClick={handleBack}>
								<ArrowBack sx={{ color: theme.palette.primary.main }} />
							</IconButton>
						) : null}
						{localLang.heading}
					</div>
					<IconButton
						aria-label="close"
						onClick={handleModalClose}
					>
						{loading ? null : <Close />}
					</IconButton>
				</CustomDialogTitle>
				<CustomDialogContent>
					{editSelect === "home" ? (
						<ImagePreviewContainer>
							{/* Let's show the selected cover image */}
							<div style={{ display: "flex", position: "relative", width: "100%" }}>
								<Chip
									label="Cover photo"
									sx={{
										position: "absolute",
										top: "0.5rem",
										left: "0.5rem",
										backgroundColor: "#FFF",
										color: "#7E7D7D",
										fontSize: "0.75rem",
										fontWeight: 400,
										borderRadius: "0.75rem",
										boxShadow: "0px 4px 18px rgba(0,0,0,0.05)",
										height: "20px",
										zIndex: 1,
									}}
								/>
								<PreviewImage
									src={originalImage[priority]}
									alt="Property Images"
									referrerPolicy="no-referrer"
									style={{ width: "100%", height: "10.5rem", borderRadius: "16px" }}
								/>
							</div>

							{order.map((index: number, position: number) => ({ file: originalImage[index] || "", index, position })).map(({ file, index, position }) => (
								<ContentContainer key={String(propertyData.images[index]?.file_id || index)}>
									{file !== "" ? (
										<ImageContainer onClick={() => movePhoto(index, 0)}>
											<PreviewImage
												alt="Property Images"
												src={file}
												referrerPolicy="no-referrer"
											/>
											<IconButton
												size="small"
												// disabled={show ? true : false}
												onClick={(event) => {
													event.stopPropagation();
													handleCrop(index);
												}}
												sx={{
													padding: "0.5rem",
													position: "absolute",
													top: "0.3rem",
													left: "0.5rem",
													zIndex: 1,
													background: "var(--Other-New-Backdrop-Overlay, rgba(0, 0, 0, 0.50))",
												}}
											>
												<CropIcon
													sx={{
														color: theme.palette.primary.main,
														fontSize: "1.25rem",
													}}
												/>
											</IconButton>

											{/* Delete image button */}
											<IconButton
												size="small"
												onClick={(event) => {
													event.stopPropagation();
													handleDeleteImage(index);
												}}
												sx={{
													position: "absolute",
													top: "0.3rem",
													right: "0.5rem",
													background: "#FFFFFF90",
													padding: "0.5rem",
													height: "2rem",
													width: "2rem",
													zIndex: 1,
													margin: "0.5rem 0rem 0rem 0rem",
												}}
											>
												<DeleteOutline sx={{ color: "#E31B0C", fontSize: "1.25rem" }} />
											</IconButton>

											{/* Move earlier / later */}

											{order.length > 1 ? (
												<div
													style={{
														position: "absolute",
														top: "0.8rem",
														left: "50%",
														transform: "translateX(-50%)",
														display: "flex",
														gap: "0.25rem",
														zIndex: 1,
													}}
												>
													<IconButton
														size="small"
														aria-label="Move earlier"
														disabled={position === 0}
														onClick={(event) => {
															event.stopPropagation();
															movePhoto(index, position - 1);
														}}
														sx={{ background: "#FFFFFFCC", height: "1.75rem", width: "1.75rem", "&:hover": { background: "#FFFFFF" } }}
													>
														<ChevronLeft sx={{ fontSize: "1.25rem" }} />
													</IconButton>
													<IconButton
														size="small"
														aria-label="Move later"
														disabled={position === order.length - 1}
														onClick={(event) => {
															event.stopPropagation();
															movePhoto(index, position + 1);
														}}
														sx={{ background: "#FFFFFFCC", height: "1.75rem", width: "1.75rem", "&:hover": { background: "#FFFFFF" } }}
													>
														<ChevronRight sx={{ fontSize: "1.25rem" }} />
													</IconButton>
												</div>
											) : null}

											<FormControlContainer
												size="small"
												onClick={(event) => event.stopPropagation()}
											>
												<Select
													value={
														category[index]
															? formatCategory(category[index])
															: formatCategory(propertyData.images[index].category)
													}
													defaultValue={category[index]}
													displayEmpty
													renderValue={(selected) => {
														if (selected === "all_images") {
															return <em style={{ fontSize: "0.875rem" }}>Category</em>;
														}

														return selected;
													}}
													inputProps={{
														placeholder: "Category",
													}}
													onChange={(event) => {
														handleSelect(event, index), setSave(true);
													}}
													sx={{
														width: "9rem",
														backgroundColor: "#FFFFFF",
													}}
													MenuProps={{
														disableScrollLock: true,
														MenuListProps: {
															sx: {
																backgroundColor: theme.palette.background.paper,
															},
														},
													}}
												>
													<MenuItem value={"Exterior"}>Exterior</MenuItem>
													<MenuItem value={"Interior"}>Interior</MenuItem>
													<MenuItem value={"Master Plan"}>Master Plan</MenuItem>
													<MenuItem value={"Amenities"}>Amenities</MenuItem>
													<MenuItem value={"Route Map"}>Route Map</MenuItem>
												</Select>
											</FormControlContainer>
										</ImageContainer>
									) : null}
								</ContentContainer>
							))}
						</ImagePreviewContainer>
					) : null}

					{/* Crop Image */}
					{editSelect === "edit-crop" ? (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								height: "100%",
							}}
						>
							{duri.length === 0 ? (
								<CropContainer
									crop={crop}
									aspect={16 / 9}
									keepSelection={true}
									ruleOfThirds={true}
									onChange={onCropChange}
									onComplete={onCropComplete}
								>
									<ImageContainer sx={{ width: "100%" }}>
										<Images
											sx={{
												width: "100%",
												height: "100%",
												objectFit: "contain",
											}}
											src={ogUrl}
											alt="Property"
											loading="lazy"
											referrerPolicy="no-referrer"
										/>
									</ImageContainer>
								</CropContainer>
							) : null}
							{duri.length === 0 ? (
								<div>
									{saveCrop ? (
										<CircularProgress
											sx={{
												width: "100%",
												height: "100%",
												position: "absolute",
												top: "50%",
												left: "50%",
												transform: "translate(-50%, -50%)",
											}}
										/>
									) : null}
								</div>
							) : (
								<div>
									<TextContainer>
										<InputContaiiner sx={{ gap: "0.5rem" }}>
											<Typography>{localLang.originalImage}</Typography>
											<Images
												sx={{
													width: "100%",
													height: "100%",
													objectFit: "contain",
												}}
												src={imagesCleanedUp[number].url}
												alt="Property"
												loading="lazy"
												referrerPolicy="no-referrer"
											/>
										</InputContaiiner>
										<InputContaiiner sx={{ gap: "0.5rem" }}>
											<Typography>{localLang.croppedImage}</Typography>
											<Images
												sx={{
													width: "100%",
													height: "100%",
													objectFit: "contain",
												}}
												src={duri}
												alt="Property"
												loading="lazy"
												referrerPolicy="no-referrer"
											/>
										</InputContaiiner>
									</TextContainer>
									{!show ? (
										""
									) : (
										<ProgressBar
											value={percentage[0]}
											size={(selectedFiles[0].file.size / 1024 / 1024) * (percentage[0] / 100)}
											stage={stage[0]}
											crop={"not-crop"}
										/>
									)}
								</div>
							)}
						</div>
					) : null}
				</CustomDialogContent>

				{editSelect === "home" ? (
					<CustomDialogActions>
						<BeegruButton
							variant="contained"
							flavor="primary"
							size="large"
							onClick={handleUploadModalOpen}
						>
							{localLang.uploadImages}
						</BeegruButton>
						{deleted ? (
							<CircularProgress />
						) : (
							<BeegruButton
								disabled={save ? false : true}
								variant="contained"
								flavor="primary"
								size="large"
								onClick={handleSaveTextData}
							>
								{localLang.save}
							</BeegruButton>
						)}
					</CustomDialogActions>
				) : null}

				{editSelect === "edit-crop" ? (
					!show ? (
						<CustomDialogActions>
							{duri.length === 0 ? (
								<BeegruButton
									variant="contained"
									flavor="primary"
									size="large"
									onClick={handleEdit}
									disabled={is_cropped}
								>
									{localLang.crop}
								</BeegruButton>
							) : null}

							<BeegruButton
								disabled={selectedFiles.length === 0 ? true : false}
								variant="contained"
								flavor="primary"
								size="large"
								onClick={async (event: React.MouseEvent<HTMLElement>) => {
									handleUpload();
								}}
							>
								{localLang.save}
							</BeegruButton>
						</CustomDialogActions>
					) : (
						<CustomDialogActions>{loading ? <CircularProgress /> : null}</CustomDialogActions>
					)
				) : null}
				<UploadImagesMobile
					open={openProfileModal}
					handleClose={handleUploadModalClose}
					propertyData={propertyData}
					session={session}
					index={0}
					dispatch={dispatch}
					isEditor={isEditor}
					profile_context={profile_context}
					property_snackbar={property_snackbar as PropertySnackbarInterface}
				/>
			</CustomDialog>
		</React.Fragment>
	);
};

export default EditModalMobile;
